import { lazy } from 'react';

/**
 * React.lazy for route chunks that survives a deploy.
 *
 * Every build renames its chunks (static/js/<id>.<hash>.js) and a deploy removes
 * the old ones. A tab that loaded main.js before the deploy still asks for the old
 * names the first time it reaches a lazy route, the server answers with index.html,
 * and webpack throws "Loading chunk N failed" — which the top-level ErrorBoundary
 * turned into a dead end for the candidate. Reloading picks up the new build.
 *
 * The reload happens at most once per chunk URL in a tab session, so a chunk that
 * is genuinely broken still ends at the ErrorBoundary instead of a reload loop.
 */

const RELOADED_FOR_KEY = 'incbot:chunkReloadedFor';

export const isChunkLoadError = (error) =>
    error?.name === 'ChunkLoadError' ||
    error?.code === 'CSS_CHUNK_LOAD_FAILED' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(error?.message || '');

/**
 * Reloads the page once for a failed chunk. Returns true when a reload was started.
 */
export const reloadOnceForChunkError = (error) => {
    // Offline is a network problem, not a stale build; reloading would only swap
    // the error screen for the browser's offline page.
    if (navigator.onLine === false) return false;
    const chunk = error?.request || error?.message || 'unknown';
    try {
        if (window.sessionStorage.getItem(RELOADED_FOR_KEY) === chunk) return false;
        window.sessionStorage.setItem(RELOADED_FOR_KEY, chunk);
    } catch (storageError) {
        // Without storage there is no way to know a reload already happened, so
        // don't risk a loop.
        return false;
    }
    window.location.reload();
    return true;
};

export const lazyWithRetry = (importChunk) =>
    lazy(() =>
        importChunk().catch((error) => {
            if (isChunkLoadError(error) && reloadOnceForChunkError(error)) {
                // Keep the Suspense fallback on screen until the reload replaces the page.
                return new Promise(() => {});
            }
            throw error;
        })
    );
